import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate } from '../lib/orgTimezone';
import { humanizeToken } from '../lib/inlineAttribution';
import { Badge } from '../atoms';
import { StatCard, StatCardRow, Modal } from '../organisms';
import { PortalLayout } from './PortalLayout';
import { QueryState, PortalTable, SectionTitle, money } from './primitives';

/**
 * ConsultantPortalApp (WS-3.2) — the role='consultant' surface. Three pages on
 * the new /api/consultant-portal/* endpoints (book, appeals, commissions), all
 * scoped server-side to the consultant's contact_id. This UI never touches
 * generic CRUD (a consultant is 403'd there). Tabs are local state, within the
 * S07 base:'/' routing. Each table row opens a read-only detail modal (the same
 * view-only pattern the client portal uses), so the surface is navigable, not a
 * dead-end.
 */

interface PortalUser { name: string; email: string; org_name?: string | null }

type Tab = 'book' | 'appeals' | 'commissions';
const TABS = [
  { key: 'book', label: 'My book' },
  { key: 'appeals', label: 'Appeals' },
  { key: 'commissions', label: 'Commissions' },
];

const dt = (s?: string | null) => formatDate(s);
const stageLabel = (s?: string | null) => (s ? String(s).replace(/_/g, ' ') : '—');

/** A label/value row for the read-only detail modals (mirrors PortalDetail). */
function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--color-border-subtle, #f0f0ee)', fontSize: 14 }}>
      <span style={{ color: 'var(--color-muted, #666)' }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right', minWidth: 0 }}>{value ?? '—'}</span>
    </div>
  );
}

export function ConsultantPortalApp({ user, onLogout }: { user?: PortalUser; onLogout: () => void }) {
  const [active, setActive] = useState<Tab>('book');
  return (
    <PortalLayout
      brandName={user?.org_name ?? 'Ignite'}
      portalLabel="Consultant portal"
      userLabel={user?.name}
      tabs={TABS}
      active={active}
      onSelect={(k) => setActive(k as Tab)}
      onLogout={onLogout}
    >
      {active === 'book' && <BookPage />}
      {active === 'appeals' && <AppealsPage />}
      {active === 'commissions' && <CommissionsPage />}
    </PortalLayout>
  );
}

function BookPage() {
  const q = useQuery({ queryKey: ['csp', 'book'], queryFn: ({ signal }) => api.get<any[]>('/api/consultant-portal/book', signal) });
  const rows = q.data ?? [];
  const [detail, setDetail] = useState<any | null>(null);
  return (
    <div>
      <SectionTitle>My book</SectionTitle>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyLabel="No assigned parcels yet.">
        <PortalTable
          rows={rows}
          getKey={(r) => r.id}
          onRowClick={setDetail}
          columns={[
            { header: 'Account', cell: (r) => r.account_number },
            { header: 'Parcel', cell: (r) => r.name || '—' },
            { header: 'Client', cell: (r) => r.client_name || '—' },
            { header: 'Current value', align: 'right', cell: (r) => money(r.current_market_value) },
            { header: 'Proposed', align: 'right', cell: (r) => money(r.proposed_market_value) },
            { header: 'Appeal', cell: (r) => (r.under_appeal ? <Badge variant="warning">Under appeal</Badge> : r.settled ? <Badge variant="success">Settled</Badge> : '—') },
          ]}
        />
      </QueryState>
      {detail && (
        <Modal open onClose={() => setDetail(null)} title={detail.name || detail.account_number || 'Parcel'} size="lg">
          <Row label="Account" value={detail.account_number} />
          <Row label="Parcel" value={detail.name} />
          <Row label="Client" value={detail.client_name} />
          <Row label="Current market value" value={money(detail.current_market_value)} />
          <Row label="Proposed market value" value={money(detail.proposed_market_value)} />
          <Row label="Appeal" value={detail.under_appeal ? <Badge variant="warning">Under appeal</Badge> : detail.settled ? <Badge variant="success">Settled</Badge> : 'Not under appeal'} />
        </Modal>
      )}
    </div>
  );
}

function AppealsPage() {
  const q = useQuery({ queryKey: ['csp', 'appeals'], queryFn: ({ signal }) => api.get<any[]>('/api/consultant-portal/appeals', signal) });
  const rows = q.data ?? [];
  const [detail, setDetail] = useState<any | null>(null);
  return (
    <div>
      <SectionTitle>Appeals</SectionTitle>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyLabel="No appeals in your book yet.">
        <PortalTable
          rows={rows}
          getKey={(r) => r.id}
          onRowClick={setDetail}
          columns={[
            { header: 'Parcel', cell: (r) => r.parcel_name || r.account_number },
            { header: 'Client', cell: (r) => r.client_name || '—' },
            { header: 'Year', cell: (r) => r.tax_year },
            { header: 'Stage', cell: (r) => <Badge variant="info">{stageLabel(r.lifecycle_stage)}</Badge> },
            { header: 'Hearing', cell: (r) => dt(r.hearing_date) },
            { header: 'Reduction', align: 'right', cell: (r) => money(r.reduction) },
          ]}
        />
      </QueryState>
      {detail && (
        <Modal open onClose={() => setDetail(null)} title={`Appeal — ${detail.parcel_name || detail.account_number || ''}`.trim()} size="lg">
          <Row label="Parcel" value={detail.parcel_name || detail.account_number} />
          <Row label="Client" value={detail.client_name} />
          <Row label="Tax year" value={detail.tax_year} />
          <Row label="Stage" value={<Badge variant="info">{stageLabel(detail.lifecycle_stage)}</Badge>} />
          <Row label="Hearing" value={dt(detail.hearing_date)} />
          <Row label="Reduction" value={money(detail.reduction)} />
        </Modal>
      )}
    </div>
  );
}

function CommissionsPage() {
  const q = useQuery({ queryKey: ['csp', 'commissions'], queryFn: ({ signal }) => api.get<any>('/api/consultant-portal/commissions', signal) });
  const items: any[] = q.data?.items ?? [];
  const [detail, setDetail] = useState<any | null>(null);
  const splitPct = (v: any) => (v != null ? `${Math.round(Number(v) * 100)}%` : '—');
  return (
    <div>
      <SectionTitle>Commissions</SectionTitle>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={items.length === 0} emptyLabel="No commission items yet.">
        <StatCardRow>
          <StatCard label="Total consultant share" value={money(q.data?.total_consultant_share)} accent="green" />
          <StatCard label="Line items" value={items.length} accent="info" />
        </StatCardRow>
        <div style={{ height: 16 }} />
        <PortalTable
          rows={items}
          getKey={(r) => r.id}
          onRowClick={setDetail}
          columns={[
            { header: 'Client', cell: (r) => r.client_name || '—' },
            { header: 'Parcel', cell: (r) => r.parcel_name || r.account_number || '—' },
            { header: 'Year', cell: (r) => r.year },
            { header: 'Amount', align: 'right', cell: (r) => money(r.amount) },
            { header: 'Split %', align: 'right', cell: (r) => splitPct(r.consultant_split_pct) },
            { header: 'My share', align: 'right', cell: (r) => money(r.consultant_share) },
            { header: 'Status', cell: (r) => <Badge variant="neutral">{humanizeToken(r.status || '')}</Badge> },
          ]}
        />
      </QueryState>
      {detail && (
        <Modal open onClose={() => setDetail(null)} title={`Commission — ${detail.parcel_name || detail.account_number || ''}`.trim()} size="lg">
          <Row label="Client" value={detail.client_name} />
          <Row label="Parcel" value={detail.parcel_name || detail.account_number} />
          <Row label="Year" value={detail.year} />
          <Row label="Amount" value={money(detail.amount)} />
          <Row label="Split %" value={splitPct(detail.consultant_split_pct)} />
          <Row label="My share" value={money(detail.consultant_share)} />
          <Row label="Status" value={<Badge variant="neutral">{humanizeToken(detail.status || '')}</Badge>} />
        </Modal>
      )}
    </div>
  );
}
